import { useContext, useEffect, useState } from 'react'

import { Text, Select, Menu, MultiSelect } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import { useCustomSteps } from 'src/context/CustomStepsContext'
import { GET_COMPANY_TAGS } from 'src/graphql/companyTags.graphql'
import { GET_JOB_LISTINGS } from 'src/graphql/joblistings.graphql'
import {
  ApplicantStage,
  DisqualifiedReason,
  HiringStage,
  NotInterestedReason,
} from 'src/graphql/types/applicants'
import {
  AutomatedRuleConditionKey,
  mapApplicantStageToConditionKey,
} from 'src/graphql/types/automatedRules'
import { JobListingType } from 'src/graphql/types/jobListings'
import { LinkTypeDisplay } from 'src/graphql/types/trackingLinks'
import IconAdd from 'src/icons/IconAdd'
import IconAlert from 'src/icons/IconAlert'
import IconFlag from 'src/icons/IconFlag'
import IconJobs from 'src/icons/IconJobs'
import IconLink from 'src/icons/IconLink'
import IconList from 'src/icons/IconList'
import IconMissedCall from 'src/icons/IconMissedCall'
import IconPhoneCall from 'src/icons/IconPhoneCall'
import IconSparkle from 'src/icons/IconSparkle'
import { formatSnakeValue } from 'src/lib/formatters'
import { TERMINATED_REASON_OPTIONS } from 'src/lib/terminatedReason.utils'
import { RecruitersContext } from 'src/context/RecruitersContext'

import {
  STEP_CONDITION_KEY,
  STEP_STATUS_OPTIONS,
  STEP_TYPE_OPTIONS,
} from '../utils/utils'

import RuleListRow from './atoms/RuleListRow'
import { inlineSelectStyles } from './inlineSelectStyles'

export const CONDITION_KEY_MAP = {
  [AutomatedRuleConditionKey.applicantStage]: {
    key: AutomatedRuleConditionKey.applicantStage,
    label: 'Applicant Stage',
    menuGroup: 'Applicant Management',
    description: "When the applicant's stage is:",
    isParentCondition: true,
    subConditions: [
      AutomatedRuleConditionKey.hiringStage,
      AutomatedRuleConditionKey.notInterestedReason,
      AutomatedRuleConditionKey.disqualifiedReason,
      AutomatedRuleConditionKey.terminatedReason,
    ],
    isMultipleSelect: false,
    options: Object.keys(ApplicantStage).map((stage) => ({
      label: formatSnakeValue(stage),
      value: stage,
    })),
  },
  [AutomatedRuleConditionKey.jobListingId]: {
    key: AutomatedRuleConditionKey.jobListingId,
    label: 'Job Listing',
    menuGroup: 'Job Management',
    description: 'When the applicant has applied to this job listing:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
  },
  [AutomatedRuleConditionKey.tagId]: {
    key: AutomatedRuleConditionKey.tagId,
    label: 'Tags',
    menuGroup: 'Applicant Management',
    description: 'When the applicant has any of these tags:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
  },
  [AutomatedRuleConditionKey.jobListingType]: {
    key: AutomatedRuleConditionKey.jobListingType,
    label: 'Job Listing Type',
    menuGroup: 'Job Management',
    description: 'When the applicant has applied to this job listing type:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
    options: Object.keys(JobListingType).map((type) => ({
      label: formatSnakeValue(type),
      value: type,
    })),
  },
  [AutomatedRuleConditionKey.linkType]: {
    key: AutomatedRuleConditionKey.linkType,
    label: 'Lead Source',
    menuGroup: 'Job Management',
    description: 'When the applicant has applied through this lead source:',
    isParentCondition: true,
    subConditions: [],
    options: Object.keys(LinkTypeDisplay).map((type) => ({
      label: formatSnakeValue(type),
      value: type,
    })),
    isMultipleSelect: true,
  },
  [AutomatedRuleConditionKey.callCount]: {
    key: AutomatedRuleConditionKey.callCount,
    label: 'Call Count',
    menuGroup: 'Call Management',
    description: 'When the applicant has been called this many times:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
    options: Array.from({ length: 20 }, (_, i) => ({
      label: String(i + 1),
      value: String(i + 1),
    })),
  },
  [AutomatedRuleConditionKey.hiringStage]: {
    key: AutomatedRuleConditionKey.hiringStage,
    label: 'Hiring Stage',
    menuGroup: 'Applicant Management',
    description: 'When the applicant`s hiring stage is:',
    isParentCondition: false,
    subConditions: [],
    isMultipleSelect: true,
    options: Object.keys(HiringStage).map((stage) => ({
      label: formatSnakeValue(stage),
      value: stage,
    })),
  },
  [AutomatedRuleConditionKey.missedOutboundCallCount]: {
    key: AutomatedRuleConditionKey.missedOutboundCallCount,
    label: 'Missed Outbound Call Count',
    menuGroup: 'Call Management',
    description: 'When the applicant has missed this many outbound calls:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
    options: Array.from({ length: 20 }, (_, i) => ({
      label: String(i + 1),
      value: String(i + 1),
    })),
  },
  [AutomatedRuleConditionKey.notInterestedReason]: {
    key: AutomatedRuleConditionKey.notInterestedReason,
    label: 'Not Interested Reason',
    menuGroup: 'Applicant Management',
    description: 'When the applicant has not been interested for this reason:',
    subConditions: [],
    isParentCondition: false,
    isMultipleSelect: true,
    options: Object.keys(NotInterestedReason).map((reason) => ({
      label: formatSnakeValue(reason),
      value: reason,
    })),
  },
  [AutomatedRuleConditionKey.disqualifiedReason]: {
    key: AutomatedRuleConditionKey.disqualifiedReason,
    label: 'Disqualified Reason',
    menuGroup: 'Applicant Management',
    description: 'When the applicant has been disqualified for this reason:',
    subConditions: [],
    isParentCondition: false,
    isMultipleSelect: true,
    options: Object.keys(DisqualifiedReason).map((reason) => ({
      label: formatSnakeValue(reason),
      value: reason,
    })),
  },
  [AutomatedRuleConditionKey.terminatedReason]: {
    key: AutomatedRuleConditionKey.terminatedReason,
    label: 'Terminated Reason',
    menuGroup: 'Applicant Management',
    description: 'When the applicant has been terminated for this reason:',
    subConditions: [],
    isParentCondition: false,
    isMultipleSelect: true,
    options: TERMINATED_REASON_OPTIONS,
  },
  [AutomatedRuleConditionKey.eligibleForRehire]: {
    key: AutomatedRuleConditionKey.eligibleForRehire,
    label: 'Eligible for Rehire',
    menuGroup: 'Applicant Management',
    description: 'When the applicant is eligible for rehire:',
    subConditions: [],
    isParentCondition: true,
    isMultipleSelect: false,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
  [AutomatedRuleConditionKey.recruiterId]: {
    key: AutomatedRuleConditionKey.recruiterId,
    label: 'Employer',
    menuGroup: 'Applicant Management',
    description: 'When the applicant is assigned to this employer:',
    isParentCondition: true,
    subConditions: [],
    isMultipleSelect: true,
  },
  [AutomatedRuleConditionKey.isPermanentlyDisqualified]: {
    key: AutomatedRuleConditionKey.isPermanentlyDisqualified,
    label: 'Permanently Disqualified',
    menuGroup: 'Applicant Management',
    description: 'When the applicant is permanently disqualified:',
    subConditions: [],
    isParentCondition: true,
    isMultipleSelect: false,
    options: [
      { label: 'Yes', value: 'true' },
      { label: 'No', value: 'false' },
    ],
  },
}

// Icon mapping mirrors atoms/ConditionChip.tsx KEY_ICON so the read-view chip
// and the edit-view row stay visually consistent.
const ROW_ICON_CLASS = 'h-3 w-3 fill-none stroke-current'
const ROW_ICON_CLASS_FILLED = 'h-3 w-3 fill-current'

const KEY_ICON: Record<string, () => JSX.Element> = {
  [AutomatedRuleConditionKey.tagId]: () => (
    <IconFlag className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.jobListingType]: () => (
    <IconJobs className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.jobListingId]: () => (
    <IconList className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.recruiterId]: () => (
    <IconList className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.linkType]: () => (
    <IconLink className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.callCount]: () => (
    <IconPhoneCall className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.missedOutboundCallCount]: () => (
    <IconMissedCall className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.notInterestedReason]: () => (
    <IconAlert className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.disqualifiedReason]: () => (
    <IconAlert className={ROW_ICON_CLASS} />
  ),
  [AutomatedRuleConditionKey.terminatedReason]: () => (
    <IconAlert className={ROW_ICON_CLASS} />
  ),
  [STEP_CONDITION_KEY]: () => <IconSparkle className={ROW_ICON_CLASS_FILLED} />,
}

const renderConditionIcon = (key: string) => {
  const renderer = KEY_ICON[key]
  return renderer ? renderer() : <IconAlert className={ROW_ICON_CLASS} />
}

// Keys that should render with the 'event' icon-pill style (matches the
// brief: step, callCount, missedOutboundCallCount).
const EVENT_KEYS = new Set<string>([
  STEP_CONDITION_KEY,
  AutomatedRuleConditionKey.callCount,
  AutomatedRuleConditionKey.missedOutboundCallCount,
])

const iconKindFor = (key: string): 'event' | 'default' =>
  EVENT_KEYS.has(key) ? 'event' : 'default'

// Short label shown as the row's primary text. Strips the leading "When ..."
// phrasing from the full description — the section header carries that cue.
const SHORT_LABEL_OVERRIDES: Record<string, string> = {
  [AutomatedRuleConditionKey.applicantStage]: 'Applicant Stage',
  [AutomatedRuleConditionKey.hiringStage]: 'Hiring Stage',
  [AutomatedRuleConditionKey.jobListingId]: 'Job Listing',
  [AutomatedRuleConditionKey.recruiterId]: 'Employer',
  [AutomatedRuleConditionKey.tagId]: 'Tags',
  [AutomatedRuleConditionKey.jobListingType]: 'Job Listing Type',
  [AutomatedRuleConditionKey.linkType]: 'Lead Source',
  [AutomatedRuleConditionKey.callCount]: 'Outbound Call Count',
  [AutomatedRuleConditionKey.missedOutboundCallCount]:
    'Outbound Missed Call Count',
  [AutomatedRuleConditionKey.notInterestedReason]: 'Not Interested Reason',
  [AutomatedRuleConditionKey.disqualifiedReason]: 'Disqualified Reason',
  [AutomatedRuleConditionKey.terminatedReason]: 'Terminated Reason',
}

const shortLabelFor = (key: string): string =>
  SHORT_LABEL_OVERRIDES[key] ?? CONDITION_KEY_MAP[key]?.label ?? key

const AutomatedRuleConditions = ({ conditions = [], setConditions }) => {
  const recruiters =
    (useContext(RecruitersContext) as { label: string; value: string }[]) ?? []
  // Classic conditions are everything that isn't a Step tuple condition.
  const classicConditions = conditions.filter(
    (c) => c.key !== STEP_CONDITION_KEY
  )
  const stepConditions = conditions.filter((c) => c.key === STEP_CONDITION_KEY)

  const customSteps = useCustomSteps()?.customSteps ?? []
  const builtInStepValues = new Set(STEP_TYPE_OPTIONS.map((o) => o.value))
  const stepTypeOptions = [
    ...STEP_TYPE_OPTIONS,
    ...customSteps.map((cs) => ({ label: cs.label, value: cs.customStepId })),
  ]

  // Find the unique keys in the classic conditions, then create a list of the keys
  const uniqueConditionKeys = [
    ...new Set(classicConditions.map((c) => c.key)),
  ].map((key) => CONDITION_KEY_MAP[key])
  const [conditionKeys, setConditionKeys] = useState(uniqueConditionKeys)

  useEffect(() => {
    if (conditions.length === 0) return
    const uniqueKeys = [
      ...new Set(
        conditions.filter((c) => c.key !== STEP_CONDITION_KEY).map((c) => c.key)
      ),
    ].map((key) => CONDITION_KEY_MAP[key])
    setConditionKeys(uniqueKeys)
  }, [conditions])

  const { data: jobListingsResponse } = useQuery(GET_JOB_LISTINGS, {
    variables: {
      pageInfo: {
        limit: 100,
        offset: 0,
      },
    },
  })

  const jobListingOptions = (jobListingsResponse?.jobListings?.items ?? []).map(
    (jl) => ({
      label: jl.title,
      value: jl.jobListingId,
    })
  )

  // Only ACTIVE tags are offered (the companyTags query is company-scoped and
  // ACTIVE-only). Tags already on the rule that were archived or deleted
  // since stay listed so the row still shows them and they can be removed.
  const { data: companyTagsResponse } = useQuery(GET_COMPANY_TAGS, {
    variables: { pageInfo: { limit: 200, offset: 0 } },
  })
  const [savedRuleTags, setSavedRuleTags] = useState<
    Record<string, { label: string; status: string }>
  >({})
  useEffect(() => {
    const resolved = conditions.filter(
      (c) => c.key === AutomatedRuleConditionKey.tagId && c.tag
    )
    if (resolved.length === 0) return
    setSavedRuleTags((prev) => {
      const next = { ...prev }
      resolved.forEach((c) => {
        next[c.value] = { label: c.tag.label, status: c.tag.status }
      })
      return next
    })
  }, [conditions])

  const selectedTagIds = conditions
    .filter((c) => c.key === AutomatedRuleConditionKey.tagId)
    .map((c) => c.value)
    .filter(Boolean)
  const activeTagOptions = (companyTagsResponse?.companyTags?.items ?? []).map(
    (tag) => ({ label: tag.label, value: tag.tagId })
  )
  const activeTagIds = new Set(activeTagOptions.map((o) => o.value))
  const tagOptions = [
    ...activeTagOptions,
    ...selectedTagIds
      .filter((tagId) => !activeTagIds.has(tagId))
      .map((tagId) => ({
        value: tagId,
        label: savedRuleTags[tagId]
          ? `${savedRuleTags[tagId].label} (archived)`
          : 'Deleted tag',
      })),
  ]

  const addConditionKey = (key) => {
    setConditionKeys([...conditionKeys, CONDITION_KEY_MAP[key]])
  }

  const updateConditions = (conditionKey, values) => {
    let tempConditions = []
    const foundConditionKey = CONDITION_KEY_MAP[conditionKey]

    // Rebuild conditions list
    for (const c of conditions) {
      if (c.key !== conditionKey) {
        tempConditions.push(c)
      } else if (values.length > 0) {
        values.forEach((v) =>
          tempConditions.push({ key: conditionKey, value: v })
        )
        values = []
      }
    }

    // Add new condition entries if none exist yet
    if (!conditions.some((c) => c.key === conditionKey) && values.length > 0) {
      values.forEach((v) =>
        tempConditions.push({ key: conditionKey, value: v })
      )
    }

    // Handle applicant stage subconditions
    if (
      foundConditionKey.key === AutomatedRuleConditionKey.applicantStage &&
      foundConditionKey.subConditions
    ) {
      const applicantStageValue =
        tempConditions.find((c) => c.key === conditionKey)?.value ?? null
      const automatedRuleConditionKey = applicantStageValue
        ? mapApplicantStageToConditionKey(applicantStageValue)
        : null

      // Only add subcondition placeholder if it doesn’t exist ye
      if (
        automatedRuleConditionKey &&
        !tempConditions.some((c) => c.key === automatedRuleConditionKey)
      ) {
        const index = tempConditions.findIndex((c) => c.key === conditionKey)
        tempConditions.splice(index + 1, 0, {
          key: automatedRuleConditionKey,
          value: null,
        })
      }

      // 🧹 Remove other subconditions not relevant to selected applicant stage
      const otherSubConditions = foundConditionKey.subConditions.filter(
        (c) => c !== automatedRuleConditionKey
      )
      tempConditions = tempConditions.filter(
        (c) => !otherSubConditions.includes(c.key)
      )

      // Eligible for Rehire only applies to terminated drivers; drop it when
      // the stage moves away from Terminated.
      if (applicantStageValue !== ApplicantStage.TERMINATED) {
        tempConditions = tempConditions.filter(
          (c) => c.key !== AutomatedRuleConditionKey.eligibleForRehire
        )
        setConditionKeys((keys) =>
          keys.filter(
            (k) => k.key !== AutomatedRuleConditionKey.eligibleForRehire
          )
        )
      }

      // Permanently Disqualified only applies to disqualified drivers; drop it
      // when the stage moves away from Disqualified.
      if (applicantStageValue !== ApplicantStage.DISQUALIFIED) {
        tempConditions = tempConditions.filter(
          (c) => c.key !== AutomatedRuleConditionKey.isPermanentlyDisqualified
        )
        setConditionKeys((keys) =>
          keys.filter(
            (k) => k.key !== AutomatedRuleConditionKey.isPermanentlyDisqualified
          )
        )
      }
    }

    // If the condition is a subcondition and no subconditions left after filtering, always keep the subcondition with null value
    if (
      !CONDITION_KEY_MAP[conditionKey].isParentCondition &&
      tempConditions.filter((c) => c.key === conditionKey).length === 0
    ) {
      // Put it after applicant stage condition if it exists
      const applicantStageIndex = tempConditions.findIndex(
        (c) => c.key === AutomatedRuleConditionKey.applicantStage
      )
      if (applicantStageIndex !== -1) {
        tempConditions.splice(applicantStageIndex + 1, 0, {
          key: conditionKey,
          value: null,
        })
      } else {
        tempConditions.push({ key: conditionKey, value: null })
      }
    }

    setConditions(tempConditions)
  }

  const removeConditionKey = (conditionKey) => {
    const removedConditionKey = CONDITION_KEY_MAP[conditionKey]
    let tempConditions = conditions.filter((c) => c.key !== conditionKey)
    let tempConditionKeys = conditionKeys.filter((c) => c.key !== conditionKey)
    if (removedConditionKey.subConditions) {
      removedConditionKey.subConditions.forEach((subCondition) => {
        // If the subCondition is already in the tempConditions, remove it
        tempConditions = tempConditions.filter((c) => c.key !== subCondition)
        tempConditionKeys = tempConditionKeys.filter(
          (c) => c.key !== subCondition
        )
      })
    }

    // Eligible for Rehire (Terminated) and Permanently Disqualified
    // (Disqualified) ride on the Applicant Stage condition; removing the stage
    // condition removes them too.
    if (removedConditionKey.key === AutomatedRuleConditionKey.applicantStage) {
      const stageGatedKeys = [
        AutomatedRuleConditionKey.eligibleForRehire,
        AutomatedRuleConditionKey.isPermanentlyDisqualified,
      ]
      tempConditions = tempConditions.filter(
        (c) => !stageGatedKeys.includes(c.key)
      )
      tempConditionKeys = tempConditionKeys.filter(
        (c) => !stageGatedKeys.includes(c.key)
      )
    }

    setConditionKeys(tempConditionKeys)
    setConditions(tempConditions)
  }

  const addStepCondition = () => {
    setConditions([
      ...conditions,
      {
        key: STEP_CONDITION_KEY,
        value: null,
        stepType: null,
        stepStatus: null,
      },
    ])
  }

  const updateStepConditionAt = (stepIndex, field, value) => {
    let stepCursor = 0
    const next = conditions.map((c) => {
      if (c.key !== STEP_CONDITION_KEY) return c
      const thisStepIdx = stepCursor++
      if (thisStepIdx !== stepIndex) return c
      return { ...c, [field]: value }
    })
    setConditions(next)
  }

  const updateStepTypeAt = (stepIndex, value) => {
    const isBuiltIn = builtInStepValues.has(value)
    let stepCursor = 0
    const next = conditions.map((c) => {
      if (c.key !== STEP_CONDITION_KEY) return c
      const thisStepIdx = stepCursor++
      if (thisStepIdx !== stepIndex) return c
      return {
        ...c,
        stepType: isBuiltIn ? value : 'CUSTOM',
        customStepId: isBuiltIn ? null : value,
      }
    })
    setConditions(next)
  }

  const removeStepConditionAt = (stepIndex) => {
    let stepCursor = 0
    const next = conditions.filter((c) => {
      if (c.key !== STEP_CONDITION_KEY) return true
      const thisStepIdx = stepCursor++
      return thisStepIdx !== stepIndex
    })
    setConditions(next)
  }

  // Eligible for Rehire only makes sense for terminated drivers, so it is
  // only offered once an Applicant Stage = Terminated condition is in place.
  const hasTerminatedStageCondition = conditions.some(
    (c) =>
      c.key === AutomatedRuleConditionKey.applicantStage &&
      c.value === ApplicantStage.TERMINATED
  )

  // Permanently Disqualified only makes sense for disqualified drivers, so it
  // is only offered once an Applicant Stage = Disqualified condition is in
  // place.
  const hasDisqualifiedStageCondition = conditions.some(
    (c) =>
      c.key === AutomatedRuleConditionKey.applicantStage &&
      c.value === ApplicantStage.DISQUALIFIED
  )

  const conditionGroups = Object.keys(CONDITION_KEY_MAP)
    .filter((key) => !conditions.some((condition) => condition.key === key))
    .filter((key) => CONDITION_KEY_MAP[key].isParentCondition)
    .filter(
      (key) =>
        key !== AutomatedRuleConditionKey.eligibleForRehire ||
        hasTerminatedStageCondition
    )
    .filter(
      (key) =>
        key !== AutomatedRuleConditionKey.isPermanentlyDisqualified ||
        hasDisqualifiedStageCondition
    )
    .reduce((acc, key) => {
      if (!acc[CONDITION_KEY_MAP[key].menuGroup]) {
        acc[CONDITION_KEY_MAP[key].menuGroup] = []
      }
      acc[CONDITION_KEY_MAP[key].menuGroup].push(CONDITION_KEY_MAP[key])
      return acc
    }, {})

  const renderConditionValue = (conditionKey) => {
    if (conditionKey.key === AutomatedRuleConditionKey.tagId) {
      return (
        <MultiSelect
          data={tagOptions}
          onChange={(values) => updateConditions(conditionKey.key, values)}
          value={selectedTagIds}
          required
          searchable
          size="xs"
          variant="unstyled"
          styles={inlineSelectStyles}
          placeholder="Select"
          aria-label="Tags"
        />
      )
    }
    if (conditionKey.key === AutomatedRuleConditionKey.recruiterId) {
      return (
        <MultiSelect
          data={recruiters}
          onChange={(values) => updateConditions(conditionKey.key, values)}
          value={conditions
            .filter((c) => c.key === conditionKey.key)
            .map((c) => c.value)
            .filter(Boolean)}
          required
          searchable
          size="xs"
          variant="unstyled"
          styles={inlineSelectStyles}
          placeholder="Select"
          aria-label="Employer"
        />
      )
    }
    if (conditionKey.key === AutomatedRuleConditionKey.jobListingId) {
      return (
        <MultiSelect
          data={jobListingOptions}
          onChange={(values) => updateConditions(conditionKey.key, values)}
          value={conditions
            .filter((c) => c.key === conditionKey.key)
            .map((c) => c.value)
            .filter(Boolean)}
          required
          searchable
          size="xs"
          variant="unstyled"
          styles={inlineSelectStyles}
          placeholder="Select"
        />
      )
    }
    if (conditionKey.isMultipleSelect) {
      // Sub-conditions (reason selects under Applicant Stage) are optional:
      // leaving one empty means "any", and the empty row is stripped on save.
      return (
        <MultiSelect
          data={conditionKey.options}
          onChange={(values) => updateConditions(conditionKey.key, values)}
          value={conditions
            .filter((c) => c.key === conditionKey.key)
            .map((c) => c.value)
            .filter(Boolean)}
          required={conditionKey.isParentCondition}
          searchable
          size="xs"
          variant="unstyled"
          styles={inlineSelectStyles}
          placeholder={conditionKey.isParentCondition ? 'Select' : 'Any'}
        />
      )
    }
    return (
      <Select
        data={conditionKey.options}
        value={conditions.find((c) => c.key === conditionKey.key)?.value}
        onChange={(value) => updateConditions(conditionKey.key, [value])}
        searchable
        required
        size="xs"
        variant="unstyled"
        styles={inlineSelectStyles}
        placeholder="Select"
      />
    )
  }

  const conditionsHeader = (
    <Text
      size="xs"
      fw={400}
      className="flex flex-row items-center justify-between text-doubleNickel-gray-500"
    >
      CONDITIONS
    </Text>
  )

  const addConditionMenu = (
    <Menu shadow="md" position="bottom-start" width="target" withinPortal>
      <Menu.Target>
        <button
          type="button"
          className="flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-doubleNickel-gray-300 bg-transparent px-2.5 py-2 text-[11.5px] font-medium leading-tight text-doubleNickel-gray-500 transition-colors hover:border-doubleNickel-brand-500 hover:bg-[rgba(28,147,215,0.04)] hover:text-doubleNickel-brand-500"
        >
          <IconAdd className="h-3 w-3 fill-none stroke-current" />
          <span>Add Condition</span>
        </button>
      </Menu.Target>
      <Menu.Dropdown className="max-h-[60vh] overflow-y-auto">
        {Object.keys(conditionGroups).map((group) => (
          <div key={group}>
            <Menu.Label key={group}>{group}</Menu.Label>
            {conditionGroups[group].map((condition) => (
              <Menu.Item
                className="text-xs"
                key={condition.key}
                onClick={() => addConditionKey(condition.key)}
              >
                {condition.label}
              </Menu.Item>
            ))}
          </div>
        ))}
        <Menu.Label>Step Management</Menu.Label>
        <Menu.Item className="text-xs" onClick={addStepCondition}>
          Step
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  )

  return (
    <div className="space-y-3">
      {conditionsHeader}

      <div className="flex flex-col gap-2">
        {conditionKeys.map((conditionKey, index) => (
          <RuleListRow
            key={`${conditionKey.key}-${index}`}
            icon={renderConditionIcon(conditionKey.key)}
            iconKind={iconKindFor(conditionKey.key)}
            label={shortLabelFor(conditionKey.key)}
            value={renderConditionValue(conditionKey)}
            onRemove={
              conditionKey.isParentCondition
                ? () => removeConditionKey(conditionKey.key)
                : undefined
            }
          />
        ))}

        {stepConditions.map((sc, idx) => (
          <RuleListRow
            key={`step-${idx}`}
            icon={renderConditionIcon(STEP_CONDITION_KEY)}
            iconKind="event"
            label="Step"
            value={
              <div className="flex flex-row items-center gap-2">
                <Select
                  data={stepTypeOptions}
                  value={sc.customStepId ?? sc.stepType}
                  onChange={(value) => updateStepTypeAt(idx, value)}
                  placeholder="Step type"
                  searchable
                  required
                  size="xs"
                  variant="unstyled"
                  styles={inlineSelectStyles}
                />
                <Select
                  data={STEP_STATUS_OPTIONS}
                  value={sc.stepStatus}
                  onChange={(value) =>
                    updateStepConditionAt(idx, 'stepStatus', value)
                  }
                  placeholder="Status"
                  searchable
                  required
                  size="xs"
                  variant="unstyled"
                  styles={inlineSelectStyles}
                />
              </div>
            }
            onRemove={() => removeStepConditionAt(idx)}
          />
        ))}

        {addConditionMenu}
      </div>
    </div>
  )
}

export default AutomatedRuleConditions
